import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Download, Filter, Trophy, Calendar, Target, TrendingUp } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../config/api';
import axios from 'axios';

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function ResultsReports() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    eventName: '',
    contestant: '',
    selectedEvents: []
  });
  const [viewMode, setViewMode] = useState('graph'); // 'table' or 'graph'
  const [graphData, setGraphData] = useState([]);
  const [uniqueEventNames, setUniqueEventNames] = useState([]);
  const [uniqueContestants, setUniqueContestants] = useState([]);
  const [eventsByShow, setEventsByShow] = useState([]);
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Helper function to extract contestants for a specific event
  const getEventContestants = (event) => {
    if (!event || !event.draws) return null;
    
    const contestants = [];
    event.draws.forEach(draw => {
      if (draw.contestants) {
        draw.contestants.forEach(contestant => {
          if (contestant.contestantName && !contestants.includes(contestant.contestantName)) {
            contestants.push(contestant.contestantName);
          }
        });
      }
    });
    
    return contestants.length > 0 ? contestants.join(', ') : null;
  };

  useEffect(() => {
    fetchResults();
  }, []);

  useEffect(() => {
    generateGraphData();
  }, [filters, results]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showEventDropdown && !event.target.closest('.event-dropdown')) {
        setShowEventDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showEventDropdown]);

  const fetchResults = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(getApiUrl('/api/results'), {
        headers: getAuthHeaders()
      });
      
      const resultsData = response.data || [];
      setResults(resultsData);
      
      // Extract unique event names and contestants
      const eventNames = new Set();
      const contestants = new Set();
      const eventsByShowMap = new Map();
      
      resultsData.forEach(result => {
        const showName = result.show_name || 'Unknown Show';
        const showDate = result.show_start_date || result.created_at;
        
        if (result.result_data?.events) {
          result.result_data.events.forEach(event => {
            if (event.name) {
              const eventKey = `${showName}|${event.name}`;
              eventNames.add(eventKey);
              
              // Group events by show
              if (!eventsByShowMap.has(showName)) {
                eventsByShowMap.set(showName, {
                  showName,
                  showDate: new Date(showDate),
                  events: new Set()
                });
              }
              eventsByShowMap.get(showName).events.add(eventKey);
            }
          });
        }
        // Extract contestants from all events
        result.result_data?.events?.forEach(event => {
          const eventContestants = getEventContestants(event);
          if (eventContestants) {
            // Split by comma and add each contestant individually
            eventContestants.split(', ').forEach(contestant => {
              if (contestant.trim()) {
                contestants.add(contestant.trim());
              }
            });
          }
        });
      });
      
      // Convert to array and sort by show date
      const eventsByShowArray = Array.from(eventsByShowMap.values())
        .map(show => ({
          ...show,
          events: Array.from(show.events).sort()
        }))
        .sort((a, b) => a.showDate - b.showDate);
      
      setUniqueEventNames(Array.from(eventNames).sort());
      setUniqueContestants(Array.from(contestants).sort());
      setEventsByShow(eventsByShowArray);
      
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const generateGraphData = () => {
    const filtered = applyFilters();
    setFilteredResults(filtered);
    
    // Group by event and collect all final times
    const eventData = [];
    
    filtered.forEach(result => {
      if (result.result_data?.events) {
        const showDate = new Date(result.show_start_date || result.created_at);
        const showName = result.show_name || 'Unknown Show';
        
        result.result_data.events.forEach(event => {
          const eventName = event.name || 'Unknown Event';
          const eventKey = `${showName}|${eventName}`;
          
          // Only process events that are selected (if any are selected)
          if (filters.selectedEvents.length > 0 && !filters.selectedEvents.includes(eventKey)) {
            return;
          }
          
          const times = [];
          
          // Check if event has draws array (new structure)
          if (event.draws && Array.isArray(event.draws)) {
            event.draws.forEach(draw => {
              const finalTime = draw.finalTime;
              if (finalTime && !isNaN(parseFloat(finalTime))) {
                times.push({
                  time: parseFloat(finalTime),
                  showDate,
                  showName
                });
              }
            });
          } else {
            // Fallback for old structure
            const finalTime = event.final_time_score || event.finalTimeScore || event.timerTime || event.timer;
            if (finalTime && !isNaN(parseFloat(finalTime))) {
              times.push({
                time: parseFloat(finalTime),
                showDate,
                showName
              });
            }
          }
          
          if (times.length > 0) {
            // Check if we already have data for this event (using composite key)
            const existingEvent = eventData.find(e => e.eventKey === eventKey);
            if (existingEvent) {
              existingEvent.times.push(...times);
            } else {
              eventData.push({
                eventKey,
                eventName,
                showName,
                times
              });
            }
          }
        });
      }
    });
    
    // Process each event's data
    const processedEventData = eventData.map(event => {
      // Sort times by show date
      const sortedTimes = event.times.sort((a, b) => a.showDate - b.showDate);
      
      return {
        eventKey: event.eventKey,
        eventName: event.eventName,
        showName: event.showName,
        displayName: `${event.eventName} (${event.showName})`,
        times: sortedTimes,
        averageTime: sortedTimes.reduce((sum, t) => sum + t.time, 0) / sortedTimes.length,
        minTime: Math.min(...sortedTimes.map(t => t.time)),
        maxTime: Math.max(...sortedTimes.map(t => t.time)),
        count: sortedTimes.length,
        firstShowDate: sortedTimes[0]?.showDate,
        lastShowDate: sortedTimes[sortedTimes.length - 1]?.showDate
      };
    });
    
    // Sort by first show date
    const sortedEventData = processedEventData.sort((a, b) => a.firstShowDate - b.firstShowDate);
    
    setGraphData(sortedEventData);
  };

  const applyFilters = () => {
    return results.filter(result => {
      const showDate = new Date(result.show_start_date || result.created_at);
      
      // Date filters
      if (filters.from && showDate < new Date(filters.from)) return false;
      if (filters.to && showDate > new Date(filters.to)) return false;
      
      // Event name filter (text search)
      if (filters.eventName) {
        const hasMatchingEvent = result.result_data?.events?.some(event => 
          event.name?.toLowerCase().includes(filters.eventName.toLowerCase())
        );
        if (!hasMatchingEvent) return false;
      }
      
      // Selected events filter
      if (filters.selectedEvents.length > 0) {
        const hasSelectedEvent = result.result_data?.events?.some(event => {
          const eventKey = `${result.show_name || 'Unknown Show'}|${event.name}`;
          return filters.selectedEvents.includes(eventKey);
        });
        if (!hasSelectedEvent) return false;
      }
      
      // Contestant filter - check if any event has matching contestants
      if (filters.contestant) {
        const hasMatchingContestant = result.result_data?.events?.some(event => {
          const contestants = getEventContestants(event);
          return contestants?.toLowerCase().includes(filters.contestant.toLowerCase());
        });
        if (!hasMatchingContestant) {
          return false;
        }
      }
      
      return true;
    });
  };

  const exportToCSV = () => {
    const csvRows = [];
    
    // Headers
    csvRows.push([
      'Show Date',
      'Show Name',
      'Contestant',
      'Event Name',
      'Final Time',
      'Position',
      'Earnings',
      'Points'
    ]);
    
    // Data rows
    filteredResults.forEach(result => {
      if (result.result_data?.events) {
        result.result_data.events.forEach(event => {
          // Handle new structure with draws array
          if (event.draws && Array.isArray(event.draws)) {
            event.draws.forEach(draw => {
              if (draw.finalTime) {
                csvRows.push([
                result.show_start_date || result.created_at || '',
                result.show_name || '',
                getEventContestants(event) || '',
                event.name || '',
                draw.finalTime || '',
                  draw.rank1 || draw.rank2 || '',
                  draw.earnings || '',
                  draw.points || ''
                ]);
              }
            });
          } else {
            // Fallback for old structure
            const finalTime = event.final_time_score || event.finalTimeScore || event.timerTime || event.timer;
            if (finalTime) {
              csvRows.push([
                result.show_start_date || result.created_at || '',
                result.show_name || '',
                getEventContestants(event) || '',
                event.name || '',
                finalTime || '',
                event.position || '',
                event.earnings || '',
                event.points || ''
              ]);
            }
          }
        });
      }
    });
    
    // Convert to CSV
    const csvContent = csvRows.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `results-report-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetFilters = () => {
    setFilters({
      from: '',
      to: '',
      eventName: '',
      contestant: '',
      selectedEvents: []
    });
  };

  const toggleEventSelection = (eventKey) => {
    setFilters(prev => ({
      ...prev,
      selectedEvents: prev.selectedEvents.includes(eventKey)
        ? prev.selectedEvents.filter(e => e !== eventKey)
        : [...prev.selectedEvents, eventKey]
    }));
  };

  const selectAllEvents = () => {
    const allEvents = eventsByShow.flatMap(show => show.events);
    setFilters(prev => ({
      ...prev,
      selectedEvents: allEvents
    }));
  };

  const clearAllEvents = () => {
    setFilters(prev => ({
      ...prev,
      selectedEvents: []
    }));
  };

  const maxTime = Math.max(...graphData.map(d => d.maxTime), 0);
  const minTime = Math.min(...graphData.map(d => d.minTime), 0);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-3 lg:p-6 overflow-x-hidden w-full max-w-full">
      {/* Header */}
      <div className="mb-4 sm:mb-6 max-w-full">
        <div className="flex flex-col gap-4 max-w-full">
          {/* Top row: Back button and title */}
          <div className="flex items-center gap-3 flex-wrap max-w-full">
            <button
              onClick={() => navigate('/results')}
              className="inline-flex items-center px-2 sm:px-3 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Back to Results</span>
              <span className="sm:hidden">Back</span>
            </button>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-semibold text-gray-800 flex items-center min-w-0 truncate">
              <Trophy className="w-5 h-5 sm:w-6 sm:h-6 mr-2 text-yellow-600" />
              <span className="hidden sm:inline">Results Reports</span>
              <span className="sm:hidden">Reports</span>
            </h1>
          </div>
          
          {/* Bottom row: View toggle and export button */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-wrap max-w-full">
            <div className="flex bg-gray-100 rounded-lg p-1 w-full sm:w-auto">
              <button
                onClick={() => setViewMode('table')}
                className={`flex-1 sm:flex-none px-3 py-2 rounded text-sm font-medium transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <span className="hidden sm:inline">Table</span>
                <span className="sm:hidden">📊 Table</span>
              </button>
              <button
                onClick={() => setViewMode('graph')}
                className={`flex-1 sm:flex-none px-3 py-2 rounded text-sm font-medium transition-colors ${
                  viewMode === 'graph' 
                    ? 'bg-white text-gray-900 shadow-sm' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <BarChart3 className="w-4 h-4 mr-1 inline" />
                <span className="hidden sm:inline">Graph</span>
                <span className="sm:hidden">Graph</span>
              </button>
            </div>
            <button
              onClick={exportToCSV}
              className="inline-flex items-center justify-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors w-full sm:w-auto"
            >
              <Download className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 mb-4 sm:mb-6 max-w-full">
        <button
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="flex items-center justify-between gap-2 w-full sm:pointer-events-none sm:cursor-default"
        >
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-600" />
            <h3 className="font-medium text-gray-800 text-sm sm:text-base">Filters</h3>
          </div>
          <svg
            className={`w-4 h-4 text-gray-600 transition-transform sm:hidden ${isFiltersExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-full mt-3 sm:mt-4 ${isFiltersExpanded ? '' : 'hidden sm:grid'}`}>
          <div>
            <label className="block text-xs sm:text-sm text-gray-600 mb-1">From Date</label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters(prev => ({ ...prev, from: e.target.value }))}
              className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm text-gray-600 mb-1">To Date</label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters(prev => ({ ...prev, to: e.target.value }))}
              className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm"
            />
          </div>
          <div className="relative event-dropdown sm:col-span-2 lg:col-span-1">
            <label className="block text-xs sm:text-sm text-gray-600 mb-1">Events</label>
            <button
              type="button"
              onClick={() => setShowEventDropdown(!showEventDropdown)}
              className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm text-left bg-white hover:bg-gray-50 flex items-center justify-between"
            >
              <span className="truncate">
                {filters.selectedEvents.length === 0 
                  ? 'Select events...' 
                  : `${filters.selectedEvents.length} event${filters.selectedEvents.length === 1 ? '' : 's'} selected`
                }
              </span>
              <svg className={`w-3 h-3 sm:w-4 sm:h-4 transition-transform ${showEventDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showEventDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 sm:max-h-64 overflow-y-auto">
                <div className="p-2 border-b">
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={selectAllEvents}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 flex-1 sm:flex-none"
                    >
                      Select All
                    </button>
                    <button
                      onClick={clearAllEvents}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex-1 sm:flex-none"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
                
                {eventsByShow.map((show, showIndex) => (
                  <div key={showIndex} className="border-b last:border-b-0">
                    <div className="px-2 sm:px-3 py-1 sm:py-2 bg-gray-50 text-xs font-medium text-gray-700 sticky top-0">
                      <div className="truncate">{show.showName}</div>
                      <div className="text-xs text-gray-500">{show.showDate.toLocaleDateString()}</div>
                    </div>
                    {show.events.map((eventKey, eventIndex) => {
                      const eventName = eventKey.split('|')[1]; // Extract just the event name
                      return (
                        <label
                          key={eventIndex}
                          className="flex items-center px-2 sm:px-3 py-1 sm:py-2 hover:bg-gray-50 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={filters.selectedEvents.includes(eventKey)}
                            onChange={() => toggleEventSelection(eventKey)}
                            className="mr-2 rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-3 h-3 sm:w-4 sm:h-4"
                          />
                          <span className="text-xs sm:text-sm text-gray-900 truncate">{eventName}</span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-xs sm:text-sm text-gray-600 mb-1">Contestant</label>
            <input
              type="text"
              value={filters.contestant}
              onChange={(e) => setFilters(prev => ({ ...prev, contestant: e.target.value }))}
              placeholder="Filter by contestant"
              className="w-full border rounded px-2 sm:px-3 py-2 text-xs sm:text-sm"
            />
          </div>
        </div>

        <div className={`flex justify-end gap-2 mt-3 sm:mt-4 ${isFiltersExpanded ? '' : 'hidden sm:flex'}`}>
          <button
            onClick={resetFilters}
            className="px-3 sm:px-4 py-2 border border-gray-300 rounded text-xs sm:text-sm hover:bg-gray-50"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4 mb-4 sm:mb-6 max-w-full">
        <div className="bg-white rounded-lg shadow-sm border p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 mr-1 sm:mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-gray-600 truncate">Total Results</div>
              <div className="text-sm sm:text-lg lg:text-xl font-semibold">{filteredResults.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <Target className="w-4 h-4 sm:w-5 sm:h-5 text-green-600 mr-1 sm:mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-gray-600 truncate">Events</div>
              <div className="text-sm sm:text-lg lg:text-xl font-semibold">{graphData.length}</div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 mr-1 sm:mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-gray-600 truncate">Best Time</div>
              <div className="text-sm sm:text-lg lg:text-xl font-semibold">
                {minTime > 0 ? `${minTime.toFixed(3)}s` : 'N/A'}
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-2 sm:p-3 lg:p-4">
          <div className="flex items-center">
            <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-600 mr-1 sm:mr-2 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-xs text-gray-600 truncate">Avg Time</div>
              <div className="text-sm sm:text-lg lg:text-xl font-semibold">
                {graphData.length > 0 ? `${(graphData.reduce((sum, d) => sum + d.averageTime, 0) / graphData.length).toFixed(3)}s` : 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Graph View */}
      {viewMode === 'graph' && (
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:p-4 lg:p-6 max-w-full overflow-x-hidden">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
            <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-blue-600" />
            <span className="hidden sm:inline">Final Times by Event</span>
            <span className="sm:hidden">Times by Event</span>
          </h3>
          
          {graphData.length === 0 ? (
            <div className="text-center py-8 sm:py-12 text-gray-500">
              <Target className="w-8 h-8 sm:w-12 sm:h-12 mx-auto mb-3 sm:mb-4 text-gray-300" />
              <p className="text-sm sm:text-base">No data available for the selected filters</p>
            </div>
          ) : (
            <div className="max-w-full">
              {/* Mobile: responsive graph that scales to screen width */}
              <div className="block sm:hidden">
                <div className="w-full">
                  <div className="relative h-40 w-full">
                    <svg className="w-full h-full block" viewBox="0 0 800 200" preserveAspectRatio="xMinYMid meet">
                      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                        <line key={i} x1="50" y1={40 + ratio * 140} x2="760" y2={40 + ratio * 140} stroke="#e5e7eb" strokeWidth="1" />
                      ))}
                      {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                        <text key={i} x="45" y={45 + ratio * 140} textAnchor="end" className="text-xs fill-gray-600" fontSize="10">
                          {(minTime + (maxTime - minTime) * (1 - ratio)).toFixed(3)}s
                        </text>
                      ))}
                      <polyline
                        fill="none"
                        stroke="#3b82f6"
                        strokeWidth="2"
                        points={graphData.map((data, index) => {
                          const denominator = Math.max(1, graphData.length - 1);
                          const x = graphData.length === 1 ? 400 : 70 + (index * 680) / denominator;
                          const y = 40 + ((maxTime - data.averageTime) / (maxTime - minTime || 1)) * 140;
                          return `${x},${y}`;
                        }).join(' ')}
                      />
                      {graphData.map((data, index) => {
                        const denominator = Math.max(1, graphData.length - 1);
                        const x = graphData.length === 1 ? 400 : 70 + (index * 680) / denominator;
                        const y = 40 + ((maxTime - data.averageTime) / (maxTime - minTime || 1)) * 140;
                        return (
                          <g key={index}>
                            <circle cx={x} cy={y} r="3" fill="#3b82f6" className="hover:fill-blue-600 cursor-pointer" />
                            <title>{data.displayName}: {data.averageTime.toFixed(3)}s ({data.count} runs)</title>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                  {/* Mobile label chips - flex wrap to avoid horizontal scroll */}
                  <div className="mt-2 px-1">
                    <div className="flex gap-2 flex-wrap">
                      {graphData.map((data, index) => {
                        const eventShort = data.eventName.substring(0, 3);
                        const showShort = data.showName.substring(0, 3);
                        const compactLabel = `${eventShort} (${showShort})`;
                        return (
                          <div key={index} className="text-xs text-gray-600 px-2 py-1 bg-gray-50 rounded border" title={`${data.eventName} - ${data.showName}`}>
                            {compactLabel}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tablet/Desktop: existing layout */}
              <div className="hidden sm:block">
                <div className="relative h-48 lg:h-64 w-full max-w-full min-w-0 overflow-hidden">
                  <svg className="w-full h-full block" viewBox="0 0 800 200" preserveAspectRatio="xMidYMid meet">
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                      <line key={i} x1="50" y1={40 + ratio * 140} x2="760" y2={40 + ratio * 140} stroke="#e5e7eb" strokeWidth="1" />
                    ))}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
                      <text key={i} x="45" y={45 + ratio * 140} textAnchor="end" className="text-xs fill-gray-600" fontSize="10">
                        {(minTime + (maxTime - minTime) * (1 - ratio)).toFixed(3)}s
                      </text>
                    ))}
                    <polyline
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="2"
                      points={graphData.map((data, index) => {
                        const denominator = Math.max(1, graphData.length - 1);
                        const x = graphData.length === 1 ? 400 : 70 + (index * 680) / denominator;
                        const y = 40 + ((maxTime - data.averageTime) / (maxTime - minTime || 1)) * 140;
                        return `${x},${y}`;
                      }).join(' ')}
                    />
                    {graphData.map((data, index) => {
                      const denominator = Math.max(1, graphData.length - 1);
                      const x = graphData.length === 1 ? 400 : 70 + (index * 680) / denominator;
                      const y = 40 + ((maxTime - data.averageTime) / (maxTime - minTime || 1)) * 140;
                      return (
                        <g key={index}>
                          <circle cx={x} cy={y} r="3" fill="#3b82f6" className="hover:fill-blue-600 cursor-pointer" />
                          <title>{data.displayName}: {data.averageTime.toFixed(3)}s ({data.count} runs)</title>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                <div className="flex justify-between mt-2 px-2 lg:px-4 overflow-hidden">
                  {graphData.map((data, index) => {
                    const labelWidth = Math.max(60, Math.min(100, 100 / graphData.length));
                    const eventShort = data.eventName.substring(0, 3);
                    const showShort = data.showName.substring(0, 3);
                    const compactLabel = `${eventShort} (${showShort})`;
                    return (
                      <div key={index} className="text-xs text-gray-600 text-center -rotate-45 origin-left flex-shrink-0" style={{ width: `${labelWidth}%`, marginLeft: index === 0 ? '0' : `-${labelWidth / 2}%` }} title={`${data.eventName} - ${data.showName}`}>
                        {compactLabel}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="mt-2 sm:mt-4 text-center text-xs sm:text-sm text-gray-600">
                Y-axis: {minTime.toFixed(3)}s - {maxTime.toFixed(3)}s
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          {/* Mobile Card View */}
          <div className="block sm:hidden">
            {filteredResults.map((result, resultIndex) => 
              result.result_data?.events?.filter(event => {
                // If no events are selected, show all events
                if (filters.selectedEvents.length === 0) return true;
                // Otherwise, only show selected events
                const eventKey = `${result.show_name || 'Unknown Show'}|${event.name}`;
                return filters.selectedEvents.includes(eventKey);
              }).map((event, eventIndex) => {
                // Handle new structure with draws array
                if (event.draws && Array.isArray(event.draws)) {
                  return event.draws.map((draw, drawIndex) => (
                    <div key={`${resultIndex}-${eventIndex}-${drawIndex}`} className="border-b border-gray-200 p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {event.name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {result.show_name || 'N/A'}
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="text-sm font-semibold text-gray-900">
                            {draw.finalTime ? `${parseFloat(draw.finalTime).toFixed(3)}s` : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {draw.rank1 || draw.rank2 || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{formatDate(result.show_start_date || result.created_at)}</span>
                        <span className="text-right max-w-32 truncate" title={getEventContestants(event) || 'N/A'}>
                          {getEventContestants(event) || 'N/A'}
                        </span>
                      </div>
                    </div>
                  ));
                } else {
                  // Fallback for old structure
                  const finalTime = event.final_time_score || event.finalTimeScore || event.timerTime || event.timer;
                  return (
                    <div key={`${resultIndex}-${eventIndex}`} className="border-b border-gray-200 p-3">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 truncate">
                            {event.name || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500 truncate">
                            {result.show_name || 'N/A'}
                          </div>
                        </div>
                        <div className="text-right ml-2">
                          <div className="text-sm font-semibold text-gray-900">
                            {finalTime ? `${parseFloat(finalTime).toFixed(3)}s` : 'N/A'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {event.position || 'N/A'}
                          </div>
                        </div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>{formatDate(result.show_start_date || result.created_at)}</span>
                        <span className="text-right max-w-32 truncate" title={getEventContestants(event) || 'N/A'}>
                          {getEventContestants(event) || 'N/A'}
                        </span>
                      </div>
                    </div>
                  );
                }
              }).flat()
            ).flat()}
          </div>

          {/* Desktop Table View */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 lg:px-4 py-3 text-left font-medium text-gray-700">Show Date</th>
                  <th className="px-3 lg:px-4 py-3 text-left font-medium text-gray-700">Show Name</th>
                  <th className="px-3 lg:px-4 py-3 text-left font-medium text-gray-700">Contestant</th>
                  <th className="px-3 lg:px-4 py-3 text-left font-medium text-gray-700">Event</th>
                  <th className="px-3 lg:px-4 py-3 text-right font-medium text-gray-700">Final Time</th>
                  <th className="px-3 lg:px-4 py-3 text-center font-medium text-gray-700">Position</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredResults.map((result, resultIndex) => 
                  result.result_data?.events?.filter(event => {
                    // If no events are selected, show all events
                    if (filters.selectedEvents.length === 0) return true;
                    // Otherwise, only show selected events
                    const eventKey = `${result.show_name || 'Unknown Show'}|${event.name}`;
                    return filters.selectedEvents.includes(eventKey);
                  }).map((event, eventIndex) => {
                    // Handle new structure with draws array
                    if (event.draws && Array.isArray(event.draws)) {
                      return event.draws.map((draw, drawIndex) => (
                        <tr key={`${resultIndex}-${eventIndex}-${drawIndex}`} className="hover:bg-gray-50">
                          <td className="px-3 lg:px-4 py-3 text-gray-900">
                            {formatDate(result.show_start_date || result.created_at)}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-900">
                            {result.show_name || 'N/A'}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-900" title={getEventContestants(event) || 'N/A'}>
                            <span className="truncate block max-w-32">
                              {getEventContestants(event) || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-900">
                            {event.name || 'N/A'}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-right text-gray-900">
                            {draw.finalTime ? `${parseFloat(draw.finalTime).toFixed(3)}s` : 'N/A'}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-center text-gray-900">
                            {draw.rank1 || draw.rank2 || 'N/A'}
                          </td>
                        </tr>
                      ));
                    } else {
                      // Fallback for old structure
                      const finalTime = event.final_time_score || event.finalTimeScore || event.timerTime || event.timer;
                      return (
                        <tr key={`${resultIndex}-${eventIndex}`} className="hover:bg-gray-50">
                          <td className="px-3 lg:px-4 py-3 text-gray-900">
                            {formatDate(result.show_start_date || result.created_at)}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-900">
                            {result.show_name || 'N/A'}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-900" title={getEventContestants(event) || 'N/A'}>
                            <span className="truncate block max-w-32">
                              {getEventContestants(event) || 'N/A'}
                            </span>
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-gray-900">
                            {event.name || 'N/A'}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-right text-gray-900">
                            {finalTime ? `${parseFloat(finalTime).toFixed(3)}s` : 'N/A'}
                          </td>
                          <td className="px-3 lg:px-4 py-3 text-center text-gray-900">
                            {event.position || 'N/A'}
                          </td>
                        </tr>
                      );
                    }
                  }).flat()
                ).flat()}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
